import {
  BidV2Status,
  type ReceivedBidV2Dto,
  type SelectionCriterionDto,
} from "../../../../../autogen/bff-api";
import { composeCriterionKey } from "../../../../../common/qualification/criterionKey";

export type TenderQualificationState = "QUALIFIED" | "REJECTED" | "PENDING";

export interface TenderQualificationRow {
  bidId: string;
  organizationId: string;
  supplierName: string;
  state: TenderQualificationState;
  reviewed: number;
  total: number;
}

export interface TenderQualificationSummary {
  rows: TenderQualificationRow[];
  qualified: number;
  rejected: number;
  pending: number;
  /** Every delivered tender is either qualified or rejected, and at least one was delivered. */
  isResolved: boolean;
}

export const deriveTenderQualification = (
  criteria: SelectionCriterionDto[],
  bids: ReceivedBidV2Dto[],
): TenderQualificationSummary => {
  const criterionKeys = criteria.map(composeCriterionKey);
  const rows = bids.map((bid) => toRow(criterionKeys, bid));
  const counts = (state: TenderQualificationState) => rows.filter((row) => row.state === state).length;
  const pending = counts("PENDING");
  return {
    rows,
    qualified: counts("QUALIFIED"),
    rejected: counts("REJECTED"),
    pending,
    isResolved: rows.length > 0 && pending === 0,
  };
};

const toRow = (criterionKeys: string[], bid: ReceivedBidV2Dto): TenderQualificationRow => {
  const verdictByKey = new Map(
    (bid.qualificationResponses ?? []).map((response) => [response.criterionKey, response.buyerVerdict] as const),
  );
  return {
    bidId: bid.id,
    organizationId: bid.owner,
    supplierName: bid.ownerName,
    state: deriveState(bid),
    reviewed: criterionKeys.filter((key) => verdictByKey.get(key) != null).length,
    total: criterionKeys.length,
  };
};

const deriveState = (bid: ReceivedBidV2Dto): TenderQualificationState => {
  if (bid.status === BidV2Status.Rejected) return "REJECTED";
  return bid.qualifiedAt ? "QUALIFIED" : "PENDING";
};
